import { useMemo, useRef } from 'react';
import { useCreditRates } from '../../../features/organizations/hooks';
import { useGenerationSettings } from '../state/settingsContext';
import {
  GENERATION_MODEL_SPEED,
  GENERATION_MODEL_QUALITY,
  DEFAULT_GENERATION_MODEL,
  RESOURCE_TYPE_IMAGE_SPEED,
  RESOURCE_TYPE_IMAGE_SPEED_2K,
  RESOURCE_TYPE_IMAGE_SPEED_4K,
  RESOURCE_TYPE_IMAGE_QUALITY_2K,
  RESOURCE_TYPE_IMAGE_QUALITY_4K,
  resolveVideoResourceType,
  type VideoModelType,
} from '../state/useGenerationState';

function findImageResourceType(
  model: string | undefined,
  imageSize: string | undefined,
): string | null {
  const m = model || DEFAULT_GENERATION_MODEL;
  if (m === GENERATION_MODEL_SPEED) {
    // 512px は廃止済み (2026-10-08)。万一残っていても 1K 料率で表示する
    if (imageSize === '4K') return RESOURCE_TYPE_IMAGE_SPEED_4K;
    if (imageSize === '2K') return RESOURCE_TYPE_IMAGE_SPEED_2K;
    return RESOURCE_TYPE_IMAGE_SPEED;
  }
  if (m === GENERATION_MODEL_QUALITY) {
    if (imageSize === '4K') return RESOURCE_TYPE_IMAGE_QUALITY_4K;
    // 512px は高品質モード非対応だが、ドロップダウンで「切替時のポイント」を
    // 表示したいので 2K 相当にフォールバックする。
    return RESOURCE_TYPE_IMAGE_QUALITY_2K;
  }
  // OpenAI (gpt-image-*) は 1K を基準 (model_code そのまま) とし、2K/4K は
  // 接尾辞付き resource_type で原価差を反映する (backend credit_service._resolve_image_resource_type と同期)
  if (m.startsWith('gpt-image-')) {
    if (imageSize === '4K') return `${m}-4k`;
    if (imageSize === '2K') return `${m}-2k`;
    return m;
  }
  // 既知モデル以外 (例: ライト gemini-3.1-flash-lite-image) は OpenAI と同じ規約で、
  // 1K/未指定を基準 (model_code そのまま) とし 2K/4K のみ接尾辞を付ける
  // (backend credit_service._resolve_image_resource_type と同期)。
  // ※旧実装の `{model}-1k` は DB の無接尾 resource_type と一致せず、ポイントが
  //   '...' や直前モデルの残留値になる不具合があった。
  if (imageSize === '4K') return `${m}-4k`;
  if (imageSize === '2K') return `${m}-2k`;
  return m;
}

/**
 * 画像生成・動画生成のポイント消化額を算出する共通フック。
 * GenerationPreviewPanel と HypeAppHomePage から同じロジックを参照するために集約。
 */
export function useGenerationCredits() {
  const { settings, videoSettings } = useGenerationSettings();
  const { data: creditRatesData } = useCreditRates();

  const findRate = (service: 'image_generation' | 'video_generation', resourceType: string | null): number | null => {
    if (!creditRatesData?.rates || !resourceType) return null;
    const rate = creditRatesData.rates.find(
      (r) => r.service_type === service && r.resource_type === resourceType,
    );
    return rate?.credits_per_unit ?? null;
  };

  // 現在設定での画像ポイント
  const imageCredits = useMemo<number | null>(() => {
    return findRate('image_generation', findImageResourceType(settings.generation_model, settings.image_size));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditRatesData, settings.generation_model, settings.image_size]);

  // 各モードのレート（現在の image_size を基準にする。GenerationPreviewPanel の挙動に準拠）
  const qualityModeCredits = useMemo<number | null>(() => {
    const rt = settings.image_size === '4K' ? RESOURCE_TYPE_IMAGE_QUALITY_4K : RESOURCE_TYPE_IMAGE_QUALITY_2K;
    return findRate('image_generation', rt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditRatesData, settings.image_size]);

  const speedModeCredits = useMemo<number | null>(() => {
    let rt: string;
    if (settings.image_size === '4K') rt = RESOURCE_TYPE_IMAGE_SPEED_4K;
    else if (settings.image_size === '2K') rt = RESOURCE_TYPE_IMAGE_SPEED_2K;
    else rt = RESOURCE_TYPE_IMAGE_SPEED;
    return findRate('image_generation', rt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditRatesData, settings.image_size]);

  // 動画生成ポイント（現在の videoSettings 基準）。
  // credits_per_unit は「1 秒あたり」のレートなので、プルダウンで選択中の秒数を掛ける。
  const videoCredits = useMemo<number | null>(() => {
    const rt = resolveVideoResourceType(
      videoSettings.videoModel,
      videoSettings.resolution,
      videoSettings.generateAudio,
    );
    const rate = findRate('video_generation', rt);
    if (rate === null) return null;
    return rate * videoSettings.duration;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditRatesData, videoSettings.videoModel, videoSettings.resolution, videoSettings.generateAudio, videoSettings.duration]);

  /** 任意の video model に対する現在解像度 × 現在音声設定でのポイント（ドロップダウン表示用） */
  const videoCreditsOf = (model: VideoModelType): number | null => {
    const rt = resolveVideoResourceType(model, videoSettings.resolution, videoSettings.generateAudio);
    return findRate('video_generation', rt);
  };

  /** 任意の image model_code に対する現在 image_size でのポイント（ドロップダウン表示用） */
  const imageCreditsOf = (modelCode: string): number | null => {
    return findRate('image_generation', findImageResourceType(modelCode, settings.image_size));
  };

  return {
    imageCredits,
    qualityModeCredits,
    speedModeCredits,
    videoCredits,
    videoCreditsOf,
    imageCreditsOf,
  };
}

/**
 * ロード中の creditRate を '...' にしつつ、直近値を保持してバッジ表示するヘルパー。
 */
export function useCreditBadgeText(credits: number | null): string | number {
  const lastRef = useRef<number | null>(null);
  return useMemo<string | number>(() => {
    if (credits !== null) {
      lastRef.current = credits;
      return credits;
    }
    if (lastRef.current !== null) return lastRef.current;
    return '...';
  }, [credits]);
}
